/**
 * Shared last-seen formatting (calendar days in local timezone).
 * Chat header uses compact style; profile uses detailed style.
 */

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export function startOfLocalDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Whole calendar days between lastSeen and now (local midnight boundaries). */
export function getCalendarDaysAgo(dateString) {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;
  const now = new Date();
  return Math.floor((startOfLocalDay(now) - startOfLocalDay(date)) / MS_PER_DAY);
}

/**
 * @param {string|null|undefined} dateString
 * @param {boolean} canViewLastSeen
 * @param {'compact'|'profile'} variant - compact: chat header; profile: user profile modal
 */
export function formatLastSeen(dateString, canViewLastSeen = true, variant = 'compact') {
  if (!canViewLastSeen) {
    return 'Last seen recently';
  }

  if (!dateString) {
    return 'Last seen recently';
  }

  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return 'Last seen recently';
  }

  const now = new Date();
  const diffMinutes = Math.floor((now - date) / (1000 * 60));
  const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
  const calendarDaysAgo = getCalendarDaysAgo(dateString);

  if (diffMinutes < 5) {
    return variant === 'profile' ? 'Online' : 'Active now';
  }

  if (calendarDaysAgo === 0) {
    if (diffMinutes < 60) {
      return `Last seen ${diffMinutes}m ago`;
    }
    const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (variant === 'profile') {
      return `Last seen today at ${timeStr}`;
    }
    return `Last seen ${diffHours}h ago`;
  }

  if (calendarDaysAgo === 1) {
    if (variant === 'profile') {
      const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      return `Last seen yesterday at ${timeStr}`;
    }
    return 'Last seen 1d ago';
  }

  if (calendarDaysAgo < 7) {
    if (variant === 'profile') {
      return `Last seen ${calendarDaysAgo} days ago`;
    }
    return `Last seen ${calendarDaysAgo}d ago`;
  }

  if (variant === 'profile') {
    return `Last seen on ${date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })}`;
  }

  const options = {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  };
  return `Last seen ${date.toLocaleDateString('en-US', options)}`;
}
